import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {getAllShifts, getShiftEarningReport, payIncentive} from "../service/api/orders";
import {
    TrendingUp, ChevronDown, Clock, Activity,
    Package, CheckCircle, AlertCircle, Calendar,
    ChevronLeft, ChevronRight, Zap, Users
} from "lucide-react";


const T = {
    bg:      "#f4f5f7",
    white:   "#ffffff",
    border:  "#e2e4e9",
    text:    "#1a1d23",
    muted:   "#8b91a1",
    subtle:  "#f8f9fb",
    blue:    "#2563eb",
    blueB:   "#dbeafe",
    green:   "#15803d",
    greenB:  "#dcfce7",
    red:     "#dc2626",
    redB:    "#fee2e2",
    amber:   "#b45309",
    amberB:  "#fef3c7",
    purple:  "#7c3aed",
    purpleB: "#ede9fe",
    sideW:   260,
};

const fmt12 = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
const fmtSec = (s) => {
    if (!s || s === 0) return "0m";
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    return h > 0 ? `${h}h` : `${m}m`;
};
const toApiDate = (ymd) => { const [y, mo, d] = ymd.split("-"); return `${d}-${mo}-${y}`; };
const initials = (name = "") => name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
const avatarBg = (name = "") => {
    const p = ["#2563eb", "#7c3aed", "#15803d", "#b45309", "#dc2626", "#0891b2", "#c026d3", "#0d9488"];
    return p[(name.charCodeAt(0) || 0) % p.length];
};
const DAY_MAP = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Compute shift period cycles
function getShiftPeriods(shift, count = 12) {
    if (!shift?.from_day || !shift?.to_day) return [];
    const fromDow = DAY_MAP[shift.from_day];
    const toDow = DAY_MAP[shift.to_day];
    const nowUtc = new Date();
    const nowIst = new Date(nowUtc.getTime() + 5.5 * 60 * 60 * 1000);
    const todayDow = nowIst.getUTCDay();
    const daysBackToFrom = (todayDow - fromDow + 7) % 7;
    const currentFrom = new Date(nowIst);
    currentFrom.setUTCDate(nowIst.getUTCDate() - daysBackToFrom);
    currentFrom.setUTCHours(0, 0, 0, 0);
    let periodLen = (toDow - fromDow + 7) % 7;
    if (periodLen === 0) periodLen = 7;
    const fmt = (d) => d.toISOString().slice(0, 10);
    const periods = [];
    for (let i = 0; i < count; i++) {
        const fromDate = new Date(currentFrom);
        fromDate.setUTCDate(currentFrom.getUTCDate() - i * 7);
        const toDate = new Date(fromDate);
        toDate.setUTCDate(fromDate.getUTCDate() + periodLen);
        const fromStr = fmt(fromDate);
        const toStr = fmt(toDate);
        const todayYmd = fmt(nowIst);
        const isComplete = toStr <= todayYmd;
        const fmtLabel = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });
        periods.push({
            from: fromStr, to: toStr,
            label: `${fmtLabel(fromStr)} – ${fmtLabel(toStr)}`,
            isComplete, isCurrent: i === 0
        });
    }
    return periods;
}

// ─ Earnings calculator ──────────────
function calcEarnings(payoutRules, activePercent) {
    if (!payoutRules?.length || activePercent == null) return null;
    const sorted = [...payoutRules].sort((a, b) => Number(a.percent) - Number(b.percent));
    let earned = null;
    for (const rule of sorted) {
        if (activePercent >= Number(rule.percent)) earned = Number(rule.amount);
    }
    return earned;
}

// ─ Avatar ─────────────────
function Avatar({ name = "", size = 34 }) {
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", background: avatarBg(name),
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: Math.round(size * 0.33), fontWeight: 800, flexShrink: 0, userSelect: "none",
            boxShadow: "0 1px 4px rgba(0,0,0,.15)",
        }}>
            {initials(name)}
        </div>
    );
}

// ─ Pill ──────────────────
function Pill({ color, children, size = "sm" }) {
    const map = {
        green: [T.green, T.greenB], red: [T.red, T.redB],
        amber: [T.amber, T.amberB], blue: [T.blue, T.blueB], purple: [T.purple, T.purpleB],
    };
    const [fg, bg] = map[color] ?? map.blue;
    return (
        <span style={{
            background: bg, color: fg, border: `1px solid ${fg}33`,
            borderRadius: 5, padding: size === "sm" ? "2px 8px" : "4px 10px",
            fontSize: size === "sm" ? 11 : 12, fontWeight: 700, whiteSpace: "nowrap",
        }}>
            {children}
        </span>
    );
}

// ─ Spinner ─────────────────
function Spin({ size = 14 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}>
            <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
            <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
        </svg>
    );
}

// ─ Calendar Period Picker ─────────────
function CalendarPicker({ periods, selectedIndex, onChange }) {
    const [open, setOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(() => {
        const p = periods[selectedIndex];
        return p ? new Date(p.from + "T00:00:00Z") : new Date();
    });
    const ref = useRef(null);

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    // Build set of valid "from" dates for quick lookup
    const validFromSet = new Set(periods.map(p => p.from));
    const periodByFrom = Object.fromEntries(periods.map((p, i) => [p.from, { ...p, index: i }]));

    const year = viewMonth.getUTCFullYear();
    const month = viewMonth.getUTCMonth();
    const firstDay = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const selectedPeriod = periods[selectedIndex];

    const isInSelectedRange = (ymd) => {
        if (!selectedPeriod) return false;
        return ymd >= selectedPeriod.from && ymd < selectedPeriod.to;
    };

    const fmtYmd = (y, mo, d) => `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

    const prevMonth = () => setViewMonth(new Date(Date.UTC(year, month - 1, 1)));
    const nextMonth = () => setViewMonth(new Date(Date.UTC(year, month + 1, 1)));

    const currentPeriod = periods[selectedIndex];

    return (
        <div ref={ref} style={{ position: "relative" }}>
            {/* Trigger button */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: open ? T.blueB : T.white,
                    border: `1px solid ${open ? T.blue : T.border}`,
                    borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 600,
                    color: open ? T.blue : T.text, cursor: "pointer", height: 36,
                    transition: "all .15s", whiteSpace: "nowrap",
                }}
            >
                <Calendar size={13} color={open ? T.blue : T.muted} />
                <span>{currentPeriod?.label ?? "Select Period"}</span>
                {currentPeriod?.isCurrent && (
                    <span style={{
                        fontSize: 9, fontWeight: 800, background: T.blue, color: "#fff",
                        borderRadius: 3, padding: "1px 5px", letterSpacing: "0.04em",
                    }}>CURRENT</span>
                )}
                {currentPeriod?.isComplete && !currentPeriod?.isCurrent && (
                    <span style={{
                        fontSize: 9, fontWeight: 800, background: T.greenB, color: T.green,
                        borderRadius: 3, padding: "1px 5px", letterSpacing: "0.04em",
                        border: `1px solid ${T.green}33`,
                    }}>DONE</span>
                )}
                <ChevronDown size={12} color={T.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", right: 0,
                    background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
                    boxShadow: "0 12px 40px rgba(0,0,0,.14)", zIndex: 300, padding: 16, width: 300,
                }}>
                    {/* Month nav */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <button onClick={prevMonth} style={{ border: "none", background: T.subtle, borderRadius: 6, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>
                            <ChevronLeft size={14} />
                        </button>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                            {MONTHS[month]} {year}
                        </span>
                        <button onClick={nextMonth} style={{ border: "none", background: T.subtle, borderRadius: 6, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>
                            <ChevronRight size={14} />
                        </button>
                    </div>

                    {/* Day headers */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
                        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                            <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: T.muted, padding: "2px 0" }}>{d}</div>
                        ))}
                    </div>

                    {/* Cells */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                        {cells.map((d, i) => {
                            if (!d) return <div key={i} />;
                            const ymd = fmtYmd(year, month, d);
                            const isValid = validFromSet.has(ymd);
                            const inRange = isInSelectedRange(ymd);
                            const isStart = selectedPeriod?.from === ymd;
                            const isEnd = selectedPeriod?.to === fmtYmd(year, month, d + 1) || false;

                            return (
                                <div
                                    key={i}
                                    onClick={() => {
                                        if (isValid) {
                                            onChange(periodByFrom[ymd].index);
                                            setOpen(false);
                                        }
                                    }}
                                    style={{
                                        aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 12, fontWeight: isStart ? 800 : inRange ? 600 : 400,
                                        borderRadius: isStart ? 6 : inRange ? 0 : 6,
                                        background: isStart ? T.blue : inRange ? T.blueB : "transparent",
                                        color: isStart ? "#fff" : inRange ? T.blue : isValid ? T.text : T.muted,
                                        cursor: isValid ? "pointer" : "default",
                                        opacity: isValid ? 1 : 0.45,
                                        transition: "background .12s",
                                    }}
                                    onMouseEnter={e => { if (isValid && !isStart) e.currentTarget.style.background = T.blueB; }}
                                    onMouseLeave={e => { if (isValid && !isStart) e.currentTarget.style.background = inRange ? T.blueB : "transparent"; }}
                                    title={isValid ? `Period starting ${ymd}` : undefined}
                                >
                                    {d}
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ marginTop: 12, padding: "8px 10px", background: T.subtle, borderRadius: 7, fontSize: 11, color: T.muted }}>
                        💡 Highlighted dates are period start dates. Click to select a period.
                    </div>
                </div>
            )}
        </div>
    );
}

// ─ Shift Dropdown ───────────────
function ShiftDropdown({ shifts, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button onClick={() => setOpen(o => !o)} style={{
                display: "flex", alignItems: "center", gap: 8, background: T.white,
                border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 12px",
                fontSize: 13, fontWeight: 600, color: T.text, cursor: "pointer", minWidth: 230, height: 36,
            }}>
                <Clock size={13} color={T.muted} />
                <span style={{ flex: 1, textAlign: "left" }}>
                    {selected ? `${selected.shift_name} · ${fmt12(selected.start_time)} – ${fmt12(selected.end_time)}` : "Select Shift"}
                </span>
                <ChevronDown size={12} color={T.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
            </button>
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%",
                    background: T.white, border: `1px solid ${T.border}`, borderRadius: 10,
                    zIndex: 200, boxShadow: "0 8px 28px rgba(0,0,0,.12)", overflow: "hidden",
                }}>
                    {shifts.map(s => (
                        <div key={s.id} onClick={() => { onChange(s); setOpen(false); }}
                             style={{
                                 padding: "10px 14px", cursor: "pointer",
                                 background: selected?.id === s.id ? T.blueB : "transparent",
                                 color: selected?.id === s.id ? T.blue : T.text,
                                 borderBottom: `1px solid ${T.border}`,
                             }}
                             onMouseEnter={e => { if (selected?.id !== s.id) e.currentTarget.style.background = T.subtle; }}
                             onMouseLeave={e => { if (selected?.id !== s.id) e.currentTarget.style.background = "transparent"; }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{s.shift_name}</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{fmt12(s.start_time)} – {fmt12(s.end_time)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─ Payout Rules chips ──────────────
function PayoutRulesChips({ shift }) {
    if (!shift?.payout_rules?.length) return null;
    const sorted = [...shift.payout_rules].sort((a, b) => Number(a.percent) - Number(b.percent));
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Payout rules:</span>
            {sorted.map((r, i) => (
                <div key={i} style={{
                    background: T.purpleB, border: `1px solid ${T.purple}33`, borderRadius: 7,
                    padding: "3px 10px", display: "flex", alignItems: "center", gap: 5,
                }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: T.purple }}>≥{r.percent}%</span>
                    <span style={{ fontSize: 11, color: T.muted }}>→</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: T.purple }}>₹{r.amount}</span>
                </div>
            ))}
        </div>
    );
}


function RiderRow({ guy, report, shift, period, onPaySuccess }) {
    const [paying, setPaying] = useState(false);
    const [paid,   setPaid]   = useState(false);
    const [payErr, setPayErr] = useState(null);

    const isLoading = report === undefined;

    if (isLoading) {
        return (
            <tr>
                <td colSpan={10} style={{ padding: "18px 20px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: T.muted, fontSize: 14 }}>
                        <Spin /> Loading {guy.name}…
                    </div>
                </td>
            </tr>
        );
    }

    if (!report) {
        return (
            <tr>
                <td colSpan={10} style={{ padding: "18px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.muted, fontSize: 14 }}>
                        <Avatar name={guy.name} size={34} />
                        <span>{guy.name} — No data</span>
                    </div>
                </td>
            </tr>
        );
    }

    // ── Pull incentive status from backend ──────────────────────────────
    const incentiveStatus = report?.incentive ?? null;
    const alreadyPaid     = incentiveStatus?.is_paid === true;

    const total         = report?.guy?.total ?? {};
    const days          = report?.days ?? {};
    const totalShiftSec = Object.values(days).reduce((s, d) => s + (d.shift_duration_seconds || 0), 0);

    // If already paid — use stored metrics from DB, skip recalculation
    const activeSec     = alreadyPaid
        ? (incentiveStatus.active_time_seconds ?? total.active_seconds ?? 0)
        : (total.active_seconds ?? 0);

    const inactiveSec   = total.inactive_seconds ?? 0;
    const activePercent = totalShiftSec > 0 ? Math.round((activeSec / totalShiftSec) * 100) : 0;

    // If already paid — use stored amount directly, skip tier calculation
    const earnings = alreadyPaid
        ? Number(incentiveStatus.amount)
        : calcEarnings(shift?.payout_rules, activePercent);

    const acceptRate = total.offered_count > 0
        ? Math.round((total.accepted_count / total.offered_count) * 100)
        : 0;

    const isPaid     = alreadyPaid || paid;
    const canPay     = period?.isComplete && earnings !== null && !isPaid;
    const isDisabled = !canPay || paying;

    const handlePay = async () => {
        if (isDisabled) return;
        setPaying(true);
        setPayErr(null);
        try {
            await payIncentive({
                shift_id: shift.id,
                user_id:  guy.id,
                started_at: period?.from,
                ended_at:   period?.to,
            });
            setPaid(true);
            onPaySuccess?.();
        } catch (e) {
            setPayErr("Failed");
        } finally {
            setPaying(false);
        }
    };

    // ── Pay cell ────────────────────────────────────────────────────────
    const payCell = (() => {
        // Already paid (from DB or just paid now)
        if (isPaid) {
            return (
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "flex-end",
                    gap: 6, color: T.green, fontSize: 13, fontWeight: 700,
                }}>
                    <CheckCircle size={14} />
                    Paid ₹{earnings ?? incentiveStatus?.amount}
                    {alreadyPaid && incentiveStatus?.paid_at && (
                        <span style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>
                            · {new Date(incentiveStatus.paid_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                        </span>
                    )}
                </div>
            );
        }

        const label   = earnings !== null ? `Pay ₹${earnings}` : "Not eligible";
        const btnBg   = isDisabled ? T.border : T.green;
        const btnColor= isDisabled ? T.muted  : "#fff";
        const title   = !period?.isComplete
            ? "Period not yet complete"
            : earnings === null
                ? "Rider did not meet minimum activity threshold"
                : "Send incentive payment";

        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <button
                    onClick={handlePay}
                    disabled={isDisabled}
                    title={title}
                    style={{
                        display: "flex", alignItems: "center", gap: 6,
                        background: btnBg, color: btnColor,
                        border: "none", borderRadius: 7,
                        padding: "6px 14px", fontSize: 13, fontWeight: 700,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                        transition: "opacity .15s, background .15s",
                        whiteSpace: "nowrap",
                    }}
                >
                    {paying ? <Spin size={12} /> : <Zap size={13} />}
                    {paying ? "Paying…" : label}
                </button>
                {payErr && (
                    <span style={{ fontSize: 11, color: T.red }}>{payErr}</span>
                )}
            </div>
        );
    })();

    return (
        <tr
            onMouseEnter={e => (e.currentTarget.style.background = T.subtle)}
            onMouseLeave={e => (e.currentTarget.style.background = isPaid ? T.greenB : "transparent")}
            style={{ background: isPaid ? `${T.greenB}88` : "transparent" }}
        >
            {/* Rider */}
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar name={guy.name} size={32} />
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{guy.name}</div>
                        {guy.phone && <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{guy.phone}</div>}
                    </div>
                    {/* Paid badge next to name */}
                    {isPaid && (
                        <span style={{
                            fontSize: 10, fontWeight: 800,
                            background: T.greenB, color: T.green,
                            border: `1px solid ${T.green}44`,
                            borderRadius: 4, padding: "2px 6px",
                        }}>
                            PAID
                        </span>
                    )}
                </div>
            </td>

            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {activeSec === 0 ? "─" : <Pill color="green">{fmtSec(activeSec)}</Pill>}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {inactiveSec === 0 ? "─" : <Pill color="red">{fmtSec(inactiveSec)}</Pill>}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {total.offered_count ? <Pill color="purple">{total.offered_count} orders</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {total.accepted_count ? <Pill color="blue">{total.accepted_count} orders</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {acceptRate ? <Pill color="amber">{acceptRate}%</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {total.done_count ? <Pill color="green">{total.done_count} orders</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {total.action_orders_count ? <Pill color="red">{total.action_orders_count} orders</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, color: T.muted }}>
                {activePercent ? <Pill color="green" size="lg">{activePercent} %</Pill> : "─"}
            </td>
            <td style={{ padding: "16px", borderTop: `1px solid ${T.border}`, textAlign: "right" }}>
                {payCell}
            </td>
        </tr>
    );
}

function SummaryBar({ reports, shift, period, onPayAllSuccess }) {
    const [paying,  setPaying]  = useState(false);
    const [payErr,  setPayErr]  = useState(null);
    const [allPaid, setAllPaid] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    if (!Array.isArray(reports) || reports.length === 0) return null;

    let totalEarnings  = 0;
    let eligibleCount  = 0;
    let alreadyPaidCount = 0;
    let totalActive    = 0;
    let totalOrders    = 0;

    reports.forEach(r => {
        const total         = r?.total ?? {};
        const days          = r?.days  ?? {};
        const incentive     = r?.incentive ?? null;
        const totalShiftSec = Object.values(days).reduce((s, d) => s + (d.shift_duration_seconds || 0), 0);

        // If already paid from DB — count separately, skip from Pay All
        if (incentive?.is_paid) {
            alreadyPaidCount++;
            totalActive += incentive.active_time_seconds ?? total.active_seconds ?? 0;
            totalOrders += total.done_count || 0;
            return;
        }

        const activeSec     = total.active_seconds || 0;
        const activePercent = totalShiftSec > 0 ? Math.round((activeSec / totalShiftSec) * 100) : 0;
        const earnings      = calcEarnings(shift?.payout_rules, activePercent);

        if (earnings !== null) {
            totalEarnings += earnings;
            eligibleCount++;
        }
        totalActive += activeSec;
        totalOrders += total.done_count || 0;
    });

    // All paid = DB already paid everyone + any just paid this session
    const effectiveAllPaid = allPaid || alreadyPaidCount === reports.length;

    const canPayAll  = period?.isComplete && eligibleCount > 0 && !effectiveAllPaid;
    const isDisabled = !canPayAll || paying;

    const disabledReason = effectiveAllPaid
        ? "All riders already paid"
        : !period?.isComplete
            ? "Period not yet complete"
            : eligibleCount === 0
                ? "No eligible riders"
                : null;

    const handlePayAll = async () => {
        if (isDisabled) return;
        if (!window.confirm(`Pay ₹${totalEarnings.toLocaleString("en-IN")} to ${eligibleCount} eligible rider(s)?`)) return;
        setPaying(true);
        setPayErr(null);
        try {
            await payIncentive({ shift_id: shift.id, started_at: period?.from, ended_at: period?.to });
            setAllPaid(true);
            onPayAllSuccess?.();
        } catch {
            setPayErr("Payment failed. Please retry.");
        } finally {
            setPaying(false);
        }
    };

    const stats = [
        { label: "Total Payout",     value: `₹${totalEarnings.toLocaleString("en-IN")}`, color: T.green,  accent: T.greenB  },
        {
            // Show "X paid / Y eligible" so admin sees both
            label: "Status",
            value: `${alreadyPaidCount} paid · ${eligibleCount} pending`,
            color: alreadyPaidCount === reports.length ? T.green : T.blue,
            accent: alreadyPaidCount === reports.length ? T.greenB : T.blueB,
        },
        { label: "Avg Active",       value: reports.length ? fmtSec(Math.round(totalActive / reports.length)) : "—", color: T.purple, accent: T.purpleB },
        { label: "Total Deliveries", value: totalOrders, color: T.amber, accent: T.amberB },
    ];

    const btnBg     = effectiveAllPaid ? T.greenB  : isDisabled ? "#f1f3f5" : T.blue;
    const btnColor  = effectiveAllPaid ? T.green   : isDisabled ? T.muted   : "#fff";
    const btnBorder = effectiveAllPaid ? `1.5px solid ${T.green}55`
        : isDisabled       ? `1.5px solid ${T.border}`
            :                    `1.5px solid ${T.blue}`;

    return (
        <div style={{ marginBottom: 18 }}>
            <div style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "stretch",
                gap: 10,
            }}>
                {/* 4 stat cards */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, minmax(0, 1fr))",
                    gap: 10,
                    flex: 1,
                }}>
                    {stats.map(({ label, value, color, accent }) => (
                        <div key={label} style={{
                            background: T.white,
                            border: `1px solid ${T.border}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            borderTop: `3px solid ${color}`,
                            display: "flex",
                            flexDirection: "column",
                            gap: 5,
                        }}>
                            <div style={{
                                fontSize: 10, fontWeight: 700, color: T.muted,
                                textTransform: "uppercase", letterSpacing: "0.07em",
                            }}>
                                {label}
                            </div>
                            <div style={{
                                fontSize: isMobile ? 16 : 20,
                                fontWeight: 800,
                                color,
                                lineHeight: 1,
                            }}>
                                {value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pay All button */}
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 4,
                    minWidth: isMobile ? "auto" : 170,
                }}>
                    <button
                        onClick={handlePayAll}
                        disabled={isDisabled}
                        title={disabledReason ?? `Pay ₹${totalEarnings.toLocaleString("en-IN")} to ${eligibleCount} riders`}
                        style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                            background: btnBg, color: btnColor, border: btnBorder,
                            borderRadius: 9,
                            padding: isMobile ? "12px 16px" : "0 18px",
                            height: isMobile ? "auto" : "100%",
                            minHeight: isMobile ? 44 : 0,
                            fontSize: 13, fontWeight: 700,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            opacity: isDisabled && !effectiveAllPaid ? 0.6 : 1,
                            transition: "all .15s", whiteSpace: "nowrap", width: "100%",
                            boxShadow: !isDisabled && !effectiveAllPaid
                                ? "0 2px 8px rgba(29,78,216,0.18)"
                                : "none",
                        }}
                    >
                        {paying          ? <><Spin size={13} /> Paying all…</>
                            : effectiveAllPaid ? <><CheckCircle size={14} /> All Paid</>
                                :                   <><Zap size={14} /> Pay All ({eligibleCount})</>}
                    </button>

                    {payErr && (
                        <div style={{
                            fontSize: 11, color: T.red, textAlign: "center",
                            padding: "2px 4px", background: T.redB,
                            borderRadius: 5, border: `1px solid ${T.red}33`,
                        }}>
                            {payErr}
                        </div>
                    )}

                    {isDisabled && !effectiveAllPaid && disabledReason && !isMobile && (
                        <div style={{ fontSize: 10, color: T.muted, textAlign: "center", lineHeight: 1.4 }}>
                            {disabledReason}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ShiftEarnings() {
    const {shiftId} = useParams();
    const navigate = useNavigate();

    const [shifts, setShifts] = useState([]);
    const [selectedShift, setSelectedShift] = useState(null);

    const [periods, setPeriods] = useState([]);
    const [periodIndex, setPeriodIndex] = useState(0);
    const selectedPeriodFrom = useRef(null);

    const [guys, setGuys] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [sendAllLoading, setSendAllLoading] = useState(false);
    const [checkedIds, setCheckedIds] = useState(new Set());
    
    useEffect(() => {
        getAllShifts().then(res => {
            if (res.success) {
                const allShifts = res.data?.shifts ?? [];
                setShifts(allShifts);
                const found = allShifts.find(s => String(s.id) === String(shiftId));
                if (found) {
                    setSelectedShift(found);
                    const ps = getShiftPeriods(found);
                    setPeriods(ps);
                    setPeriodIndex(0);
                    selectedPeriodFrom.current = ps[0]?.from ?? null;
                }
            }
        }).finally(() => setInitLoading(false));
    }, [shiftId]);

    // ─ Shift change: reload guys, recompute periods but keep date ──
    const handleShiftChange = useCallback((s) => {
        setSelectedShift(s);
        navigate(`/shifts/${s.id}/earnings`, { replace: true });
        const ps = getShiftPeriods(s);
        setPeriods(ps);
        // Try to find same period-from in new shift's periods
        const savedFrom = selectedPeriodFrom.current;
        const matchIdx = savedFrom ? ps.findIndex(p => p.from === savedFrom) : -1;
        setPeriodIndex(matchIdx >= 0 ? matchIdx : 0);
        if (matchIdx < 0 && ps[0]) selectedPeriodFrom.current = ps[0].from;
    }, [navigate]);

    useEffect(() => {
        if (!selectedShift || !periods[periodIndex]) return;

        const period = periods[periodIndex];

        setReportsLoading(true);
        setReports([]); // ✅ ALWAYS ARRAY

        getShiftEarningReport(selectedShift.id, {
            from: toApiDate(period.from),
            to: toApiDate(period.to),
        })
            .then(res => {
                if (res.success && Array.isArray(res.data)) {
                    setReports(res.data); // ✅ KEEP SORTED ARRAY FROM BACKEND
                } else {
                    setReports([]);
                }
            })
            .catch(() => {
                setReports([]);
            })
            .finally(() => setReportsLoading(false));

    }, [selectedShift, periodIndex]);


    const handlePeriodChange = (idx) => {
        if (idx < 0 || idx >= periods.length) return;
        setPeriodIndex(idx);
        selectedPeriodFrom.current = periods[idx]?.from ?? null;
    };


    const handleSendAll = async () => {
        const toSend = guys.filter(g => checkedIds.has(g.id));
        if (!toSend.length) return;
        if (!window.confirm(`Send payment to ${toSend.length} rider(s)?\n\n${toSend.map(g => g.name).join(", ")}`)) return;
        setSendAllLoading(true);
        try {
            await Promise.all(toSend.map(g => new Promise(r => setTimeout(r, 900)))); // TODO: real API
            alert(`✅ Payments sent to ${toSend.length} riders`);
            setCheckedIds(new Set());
        } finally {
            setSendAllLoading(false);
        }
    };


    const currentPeriod = periods[periodIndex];
    const eligibleCount = reports.filter(r => {
        const total = r?.total ?? {};
        const days = r?.days ?? {};

        const totalShiftSec = Object.values(days)
            .reduce((s, d) => s + (d.shift_duration_seconds || 0), 0);

        const activeSec = total.active_seconds || 0;

        const activePercent = totalShiftSec > 0
            ? Math.round((activeSec / totalShiftSec) * 100)
            : 0;

        return calcEarnings(selectedShift?.payout_rules, activePercent) !== null;
    }).length;

    if (initLoading) return (
        <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>
            <Spin size={18} /><span style={{ marginLeft: 10, fontSize: 14 }}>Loading shift earnings…</span>
        </div>
    );

    return (
        <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: T.bg, color: T.text, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                *, *::before, *::after { box-sizing: border-box; }
                ::-webkit-scrollbar { width: 5px; height: 5px; }
                ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
            `}</style>

            {/* ══ TOP BAR ══ */}
            <div style={{
                flexShrink: 0, background: T.white, borderBottom: `1px solid ${T.border}`,
                padding: "10px 20px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", zIndex: 50,
            }}>
                <ShiftDropdown shifts={shifts} selected={selectedShift} onChange={handleShiftChange} />

                {/* Payout rules inline */}
                {selectedShift && <PayoutRulesChips shift={selectedShift} />}

                <div style={{ flex: 1 }} />

                {/* Calendar period picker */}
                {periods.length > 0 && (
                    <CalendarPicker
                        periods={periods}
                        selectedIndex={periodIndex}
                        onChange={handlePeriodChange}
                    />
                )}

                <div style={{ width: 1, height: 22, background: T.border }} />

                {/* Send All */}
                {currentPeriod?.isComplete && checkedIds.size > 0 && (
                    <button
                        onClick={handleSendAll}
                        disabled={sendAllLoading}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: T.blue, color: "#fff",
                            border: "none", borderRadius: 8, padding: "7px 14px",
                            fontSize: 13, fontWeight: 700, cursor: "pointer",
                            opacity: sendAllLoading ? 0.7 : 1, height: 36,
                        }}
                    >
                        {sendAllLoading ? <Spin size={12} /> : <Zap size={13} />}
                        Send All ({checkedIds.size})
                    </button>
                )}

                <div style={{
                    display: "flex", alignItems: "center", gap: 6, background: T.greenB,
                    color: T.green, border: `1px solid ${T.green}33`, borderRadius: 8,
                    padding: "6px 13px", fontSize: 12, fontWeight: 700, height: 36,
                }}>
                    <TrendingUp size={13} />
                    Earnings &amp; Incentives
                </div>
            </div>

            {/* ══ CONTENT ══ */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px 60px" }}>

                {!selectedShift && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80%", color: T.muted, gap: 10 }}>
                        <TrendingUp size={40} style={{ opacity: 0.15 }} />
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>No shift selected</div>
                        <div style={{ fontSize: 13 }}>Pick a shift from the top bar to view earnings</div>
                    </div>
                )}

                {selectedShift && (
                    <>
                        {currentPeriod && !currentPeriod.isComplete && (
                            <div style={{
                                background: T.amberB,
                                border: `1px solid ${T.amber}44`,
                                borderRadius: 8,
                                padding: "9px 14px",
                                marginBottom: 16,
                                fontSize: 12,
                                color: T.amber,
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8
                            }}>
                                <Clock size={13} />
                                Period still ongoing. Payments unlock after {currentPeriod.label.split("–")[1]?.trim()}.
                            </div>
                        )}

                        {!reportsLoading && reports?.length > 0 && (
                            <SummaryBar
                                reports={reports}
                                shift={selectedShift}
                                period={currentPeriod}
                                onPayAllSuccess={() => {}}
                            />
                        )}

                        {/* Table */}
                        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
                            {/* Table toolbar */}
                            <div style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
                                background: T.subtle,
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Users size={14} color={T.muted} />
                                    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                        {reports?.length} Riders
                                    </span>
                                    {!reportsLoading && reports?.length > 0 && (
                                        <span style={{ fontSize: 11, color: T.muted }}>
                                            · {eligibleCount} eligible for payout
                                        </span>
                                    )}
                                </div>
                                <div style={{ flex: 1 }} />
                                {reportsLoading && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted }}>
                                        <Spin size={12} /> Fetching reports…
                                    </div>
                                )}
                            </div>

                            <div style={{overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "collapse", minWidth: 1050}}>

                                    <thead>
                                    <tr style={{borderBottom: `2px solid ${T.border}`}}>
                                        {[
                                            {label: "Rider"},
                                            {label: "Active Time", color: T.text, icon: <Clock size={13}/>},
                                            {label: "Inactive", color: T.red, icon: <Clock size={13}/>},
                                            {label: "Offered", color: T.purple, icon: <Package size={13}/>},
                                            {label: "Accepted", color: T.blue, icon: <CheckCircle size={13}/>},
                                            {label: "Accepted %", color: T.yellow, icon: <CheckCircle size={13}/>},
                                            {label: "Done", color: T.green, icon: <CheckCircle size={13}/>},
                                            {label: "Action", color: T.red, icon: <AlertCircle size={13}/>},
                                            {label: "Active %", color: T.green, icon: <Activity size={13}/>},
                                            {label: "Earnings", right: true, icon: <TrendingUp size={13}/>},
                                        ].map(({label, color, icon, right}, i) => (
                                            <th
                                                key={i}
                                                style={{
                                                    padding: "12px 20px",
                                                    textAlign: right ? "right" : i === 0 ? "left" : "center",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: T.muted,
                                                    background: T.subtle,
                                                    letterSpacing: "0.04em",
                                                    textTransform: "uppercase",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: right
                                                        ? "flex-end"
                                                        : i === 0
                                                            ? "flex-start"
                                                            : "center",
                                                    gap: 6
                                                }}>
                                                    {icon && (
                                                        <span style={{color: color ?? T.muted}}>
                                {icon}
                            </span>
                                                    )}
                                                    {label}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {reportsLoading && (
                                        <tr>
                                            <td colSpan={9} style={{
                                                padding: "40px",
                                                textAlign: "center",
                                                color: T.muted,
                                                fontSize: 14
                                            }}>
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 8
                                                }}>
                                                    <Spin/> Loading riders…
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!reportsLoading && reports?.map(r => (
                                        <RiderRow
                                            key={r.delivery_guy_id}
                                            guy={{ id: r.delivery_guy_id, name: r.delivery_guy_name }}
                                            report={{ days: r.days, guy: { total: r.total }, incentive: r.incentive }}
                                            shift={selectedShift}
                                            period={currentPeriod}
                                            onPaySuccess={() => {}}
                                        />
                                    ))}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}